import React from 'react';
import { Box, FormControl, InputLabel, TextField } from '@mui/material';

function toInputValue(date) {
  if (!date || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseInputValue(value) {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  date.setHours(0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
}

function clampDate(date, minDate, maxDate) {
  if (!date) return date;
  let next = date;
  if (minDate && next < minDate) next = new Date(minDate);
  if (maxDate && next > maxDate) next = new Date(maxDate);
  next.setHours(0, 0, 0, 0);
  return next;
}

/**
 * From/To date selectors for the chart date-range filter.
 * Parent supplies the default selection (e.g. last 6 months → latest).
 * minDate/maxDate are the full selectable extent (all available data), not the default window.
 */
export default function DateRangeFilter({
  dateRange,
  onChange,
  minDate = null,
  maxDate = null,
  label = 'Date Range Filter',
}) {
  const [startDate, endDate] = dateRange || [null, null];
  const minStr = toInputValue(minDate);
  const maxStr = toInputValue(maxDate);

  if (!startDate || !endDate || Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return null;
  }

  const handleFromChange = (event) => {
    const parsed = parseInputValue(event.target.value);
    if (!parsed) return;
    let nextStart = clampDate(parsed, minDate, maxDate);
    let nextEnd = endDate;
    if (nextEnd && nextStart > nextEnd) {
      nextEnd = nextStart;
    }
    onChange([nextStart, nextEnd]);
  };

  const handleToChange = (event) => {
    const parsed = parseInputValue(event.target.value);
    if (!parsed) return;
    let nextEnd = clampDate(parsed, minDate, maxDate);
    let nextStart = startDate;
    if (nextStart && nextEnd < nextStart) {
      nextStart = nextEnd;
    }
    onChange([nextStart, nextEnd]);
  };

  const fieldSx = {
    minWidth: 140,
    '& .MuiInputBase-root': { height: 40 },
    '& input': { py: 1, fontSize: '0.875rem' },
  };

  return (
    <FormControl size="small" sx={{ minWidth: 300, position: 'relative' }}>
      <InputLabel
        shrink
        sx={{
          bgcolor: 'background.paper',
          px: 0.5,
          transform: 'translate(14px, -9px) scale(0.75)',
          transformOrigin: 'top left',
          zIndex: 1,
        }}
      >
        {label}
      </InputLabel>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          height: 40,
          px: 1,
          border: '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: 1,
          bgcolor: 'transparent',
          '&:hover': { borderColor: 'rgba(0, 0, 0, 0.87)' },
          '&:focus-within': {
            borderColor: 'primary.main',
            borderWidth: '2px',
          },
        }}
      >
        <TextField
          type="date"
          size="small"
          variant="standard"
          value={toInputValue(startDate)}
          onChange={handleFromChange}
          inputProps={{ min: minStr || undefined, max: toInputValue(endDate) || maxStr || undefined }}
          InputProps={{ disableUnderline: true }}
          sx={fieldSx}
          aria-label="From date"
        />
        <Box sx={{ color: 'text.secondary', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>to</Box>
        <TextField
          type="date"
          size="small"
          variant="standard"
          value={toInputValue(endDate)}
          onChange={handleToChange}
          inputProps={{ min: toInputValue(startDate) || minStr || undefined, max: maxStr || undefined }}
          InputProps={{ disableUnderline: true }}
          sx={fieldSx}
          aria-label="To date"
        />
      </Box>
    </FormControl>
  );
}
